import { Box, TableCell, TableRow, Typography, Tooltip, IconButton, CircularProgress } from '@mui/material';
import { Circle, Download, LocalFireDepartment } from '@mui/icons-material';

import { AdminRoles, Role } from '../../config/DiactiveTypes';
import { calculatePostEvalDate, formatDateTime, getPatientFullName, getPatientGroupName, getPatientHspId } from '../../utils/DiactiveUtils';
import { getHospitalName, getHospitalShortName } from '../../services/HospitalService';
import { formatPendingGlucoseFiles, formatPendingQuestionnairesFiles } from '../../utils/PatientsTableUtils';

const PatientsTableRow = ({
  patient,
  role,
  viewAsDoctor = false,
  showPersonal,
  showPostEvalDate = false,
  showPendingDetails,
  showNormalDetails = false,
  sessionsThisWeek = 0,
  downloadingSingle,
  onPatientClick,
  onDownloadClick,
}) => {
  const patientLabel = showPersonal ? getPatientFullName(patient) : (patient.nickname ?? "");
  const patientLabelWidth = (role === Role.Doctor || viewAsDoctor) ? 220 : 220;
  const groupLabel = getPatientGroupName(patient.role);
  const hospitalLabel = getHospitalName(patient.hospital);
  const hospitalShortLabel = getHospitalShortName(patient.hospital);
  const patientIdLabel = getPatientHspId(patient);

  const glucoseFilesDisplay = formatPendingGlucoseFiles({
    statusCode: patient.status?.code,
    glucoseFileCount: patient.glucoseFileCount,
  });

  const questionnairesFilesDisplay = formatPendingQuestionnairesFiles({
    statusCode: patient.status?.code,
    questionnairesCount: patient.questionnairesCount,
    patientRole: patient.role,
  });

  return (
    <TableRow
      key={patient.id}
      hover
      onClick={() => onPatientClick(patient.id)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
    >
      <TableCell sx={{ width: 170, maxWidth: 170 }}>
        {formatDateTime(patient.createdAt)}
      </TableCell>

      <TableCell sx={{ width: patientLabelWidth, maxWidth: patientLabelWidth }}>
        <Tooltip title={patientLabel || ''} disableHoverListener={!patientLabel}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {patientLabel || '-'}
          </Typography>
        </Tooltip>
      </TableCell>

      {AdminRoles.includes(role) && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 210, maxWidth: 210 }}>
          <Tooltip title={patientIdLabel || ''} disableHoverListener={!patientIdLabel}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {patientIdLabel || '-'}
            </Typography>
          </Tooltip>
        </TableCell>
      )}

      {AdminRoles.includes(role) && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 160, maxWidth: 160 }}>
          <Tooltip title={hospitalLabel || ''} disableHoverListener={!hospitalLabel}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hospitalShortLabel || '-'}
            </Typography>
          </Tooltip>
        </TableCell>
      )}

      <TableCell sx={{ width: 120, maxWidth: 120 }}>
        {patient.daysRemaining}
      </TableCell>

      {showPostEvalDate && (
        <TableCell sx={{ width: 170, maxWidth: 170 }}>
          {formatDateTime(calculatePostEvalDate(patient.countdownStartedAt), 'dd/MM/yyyy')}
        </TableCell>
      )}

      <TableCell sx={{ width: 95, maxWidth: 95 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={patient.status?.label}>
            <Circle color={patient.status?.color} />
          </Tooltip>
          {sessionsThisWeek >= 3 && (
            <Tooltip title={`${sessionsThisWeek} sesiones esta semana`}>
              <LocalFireDepartment sx={{ color: '#ff6d00', fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {showPendingDetails && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 130, maxWidth: 130 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {questionnairesFilesDisplay}
          </Typography>
        </TableCell>
      )}

      {showPendingDetails && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 110, maxWidth: 110 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {glucoseFilesDisplay}
          </Typography>
        </TableCell>
      )}

      {showNormalDetails && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 100, maxWidth: 100 }}>
          <Typography variant="body2">
            {sessionsThisWeek}
          </Typography>
        </TableCell>
      )}

      <TableCell sx={{ width: 120, maxWidth: 120 }}>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {groupLabel || '-'}
        </Typography>
      </TableCell>

      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 70, maxWidth: 70 }}>
        {downloadingSingle[patient.id] ? (
          <CircularProgress size={29} />
        ) : (
          <IconButton
            onClick={(event) => onDownloadClick(event, patient.id)}
            aria-label="download"
            size="small"
          >
            <Download color="primary" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default PatientsTableRow;

